import React, { createContext, useCallback, useContext, useMemo, useReducer, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';

import { FilterProvidersTypeEnum } from 'core/FiltersDialog/components/FilterProviders/FilterProviders';
import { cloneDeep, get, isArray, isEqual, omit } from 'lodash';

import useNodesCount from 'hooks/useNodesCount';
import useReduxMutation from 'hooks/useReduxMutation';
import { actionSaveFilters } from 'sagas/sagaWatchersViewer';

import { useFiltersMessageChannelItems } from './components/FiltersMessageChannel';
import { useFiltersMessageRefItems } from './components/FiltersMessageRef';
import { useFiltersMessageStatusItems } from './components/FiltersMessageStatus';
import { useFiltersMessageTypeItems } from './components/FiltersMessageType';
import useFilters from './hooks/useFilters';
import { useFiltersCount } from './hooks/useFiltersCount';
import { ToggleDialogConfig } from './types';
import { getFiltersInitialCount } from './utils/getFiltersInitialCount';

const FiltersContext = createContext<any>({});
const DialogContext = createContext<any>({});

export const useFiltersContext = () => {
  const ctx = useContext(FiltersContext);

  if (typeof ctx === 'undefined') {
    throw new Error('useFiltersContext must be used within FiltersDialogProvider');
  }

  return ctx;
};

export const useDialogContext = () => {
  const ctx = useContext(DialogContext);

  if (typeof ctx === 'undefined') {
    throw new Error('useDialogContext must be used within FiltersDialogProvider');
  }

  return ctx;
};

function getFilterKey(filterName: string) {
  switch (filterName) {
    case 'inbound':
      return 'referral';
    case 'outbound':
      return 'outboundReferral';
    default:
      return filterName;
  }
}

type Filters = Record<string, unknown[] | Record<string, unknown>>;

interface FiltersDialogProviderProps {
  initialFilters: Filters & { filtersQueryParams: Record<string, unknown[]> };
  filterName: string;
  onApplyFilters?: (initialFilters: Filters) => void;
  isLocked?: boolean;
  hideLockButton?: boolean;
  resetFiltersShape: Filters;
  isToForceUpdateFilters?: boolean;
  newFiltersFromOutsideDialog?: any;
  details?: Record<string, unknown>;
}

export const FiltersDialogProvider: React.FC<FiltersDialogProviderProps> = ({
  children,
  initialFilters,
  filterName,
  onApplyFilters,
  isLocked = false,
  hideLockButton = false,
  resetFiltersShape, // <- This is the value used to reset the filters, like a default value.
  isToForceUpdateFilters,
  newFiltersFromOutsideDialog,
  details,
}) => {
  const { filtersQueryParams, ...filtersWithoutParams } = initialFilters;
  const queryClient = useQueryClient();
  const [execute] = useReduxMutation(actionSaveFilters);
  const dispatchSaveFilters = useCallback(
    (filterKey: string, filterValue: any) => {
      const data = { [filterKey]: filterValue };
      execute(
        {
          url: `/users/filters`,
          method: 'post',
          data,
          callbackSuccess: () => queryClient.invalidateQueries(['filters', filterName]),
        },
        { forceFetchQuery: true },
      );
    },
    [execute, queryClient, filterName],
  );

  const filtersTool = useFilters(
    filtersWithoutParams,
    onApplyFilters,
    isToForceUpdateFilters,
    newFiltersFromOutsideDialog,
  );

  // set the filters applied flag if the filters applied are different
  // from the default value for the filters
  const [hasFiltersApplied, setHasFiltersApplied] = useState(
    !isEqual(filtersTool.filters, omit(resetFiltersShape, 'filtersQueryParams')),
  );

  const messageFilters: Record<string, unknown[]> = {
    messageRef: useFiltersMessageRefItems(),
    messageStatus: useFiltersMessageStatusItems(),
    messageType: useFiltersMessageTypeItems(),
    messageChannel: useFiltersMessageChannelItems(),
  };

  const filterKeys = Object.keys(filtersWithoutParams).filter(
    (key) =>
      key !== 'dateRange' &&
      key !== 'additionalDateRange' &&
      key !== 'dateRangeFuture' &&
      key !== 'dateRangeWithoutCustom' &&
      key !== 'broadcastListDateRange' &&
      key !== 'dateAggregation',
  );

  // initialize the object for the counts
  const initialCount = Object.entries(filtersTool.filters).reduce((initial, [key, value]) => {
    if (isArray(value)) {
      const isOnMessageFilters = !!get(messageFilters, key, false);
      if (isOnMessageFilters) {
        return {
          ...initial,
          [key]: messageFilters[key].length,
        };
      }

      return getFiltersInitialCount(filterName, initial, key);
    }
    return initial;
  }, {});

  const [count, setCount] = useReducer((state: any, action: any) => ({ ...state, ...action }), initialCount);

  // The variable and handler below are used to set the custom count for the filters that are not fetched via GQL.
  const [customCount, setCustomCount] = useState({});
  const handleSetCustomCount = useCallback((key: string, value: number) => {
    setCustomCount((currentCount: any) => ({ ...currentCount, [key]: value }));
  }, []);

  const [isFilterLocked, setFilterLocked] = useState<boolean>(isLocked);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(Object.keys(filtersTool.filters)[0]);
  const filtersSnapshotOnDialogOpenRef = useRef(null);
  const filtersLockSnapshotOnDialogOpenRef = useRef<boolean | null>(null);
  const includeSchedulerProvider = filterName === 'schedule' && filterKeys.some((key) => /provider/.test(key));
  const includeTrueTimeProvider =
    filterName === FilterProvidersTypeEnum.trueTimeConfig && filterKeys.some((key) => /providers/.test(key));

  const shouldIncludeCountTo = {
    includeProvider:
      !includeSchedulerProvider && !includeTrueTimeProvider && filterKeys.some((key) => /provider/.test(key)),
    includeSchedulerProvider,
    includeTrueTimeProvider,
    includeGroup: filterKeys.some((key) => /group/.test(key)),
    includeRenderingProvider: filterKeys.some((key) => /renderingProvider/.test(key)),
    includeReferringProvider: filterKeys.some((key) => /referringProvider/.test(key)),
    includeApptType: filterKeys.some((key) => /apptType/.test(key)),
    includeFacility: filterKeys.some((key) => /facility/.test(key)),
    includeCustomAction: filterKeys.some((key) => /customAction/.test(key)),
    includeFormName: filterKeys.some((key) => /formName/.test(key)),
    includeForms: filterKeys.some((key) => /forms/.test(key)),
    includeCCDAForms: filterKeys.some((key) => /ccdaForms/.test(key)),
    includeSchedulingPlus: filterKeys.some((key) => /schedulingPlus/.test(key)),
    includeInsurance: filterKeys.some((key) => /insurance/.test(key)),
    includeSender: filterKeys.some((key) => /sender/.test(key)),
    includeApprover: filterKeys.some((key) => /approver/.test(key)),
  };

  useNodesCount(shouldIncludeCountTo, (data) => setCount({ ...data, ...customCount }));

  const restCount = useFiltersCount({ includeUser: filterKeys.includes('user'), filtersQueryParams });

  const handleApplyFilters = useCallback(() => {
    // only set the filters applied flag if the filters applied are different
    // from the default value for the filters
    setHasFiltersApplied(!isEqual(filtersTool.filters, omit(resetFiltersShape, 'filtersQueryParams')));

    const filtersPayload = {
      ...filtersTool.filters,
      locked: false,
    };

    if (isFilterLocked) filtersPayload.locked = true;

    dispatchSaveFilters(getFilterKey(filterName), filtersPayload);

    if (onApplyFilters) onApplyFilters(cloneDeep(filtersTool.filters)); // Send deepclone of the filters to avoid mutations of reference.
  }, [dispatchSaveFilters, filterName, filtersTool.filters, isFilterLocked, onApplyFilters, resetFiltersShape]);

  const toggleLockFilters = useCallback(
    (value: boolean) => {
      if (!value) {
        if (isFilterLocked) {
          dispatchSaveFilters(getFilterKey(filterName), { locked: false });
          setFilterLocked(value);
        }
        return;
      }

      setFilterLocked((currentLockFilters: boolean) => !currentLockFilters);
    },
    [dispatchSaveFilters, filterName, isFilterLocked],
  );

  const toggleDialogOpen = useCallback(
    (config: ToggleDialogConfig = {}) => {
      setDialogOpen((isOpen) => {
        if (!isOpen) {
          // Take a snapshot of current filters, so in case one cancels dialog
          // filters will go back as they were.
          filtersSnapshotOnDialogOpenRef.current = filtersTool.filters;
          filtersLockSnapshotOnDialogOpenRef.current = isFilterLocked;
        }

        if (config.shouldApplySnapshotOnClose) {
          filtersTool.reset(filtersSnapshotOnDialogOpenRef.current);
          setFilterLocked(!!filtersLockSnapshotOnDialogOpenRef.current);
        }

        return !isOpen;
      });
    },
    [filtersTool, isFilterLocked],
  );

  const changeTab = (tab: string) => {
    setSelectedTab(tab);
  };

  const tabs = Object.keys(filtersTool.filters).filter((tab) => {
    if (filterName === 'inbound') {
      return tab !== 'renderingProvider';
    }
    if (filterName === 'outbound') {
      return tab !== 'referringProvider';
    }
    return true;
  });

  const dialogValues = useMemo(
    () => ({
      toggleDialogOpen,
      dialogOpen,
      toggleLockFilters,
      isFilterLocked,
      hideLockButton,
      hasFiltersApplied,
      resetFiltersShape,
      setHasFiltersApplied,
      selectedTab,
      changeTab,
      handleApplyFilters,
      tabs,
      details,
      handleSetCustomCount,
    }),
    [
      dialogOpen,
      handleApplyFilters,
      hasFiltersApplied,
      isFilterLocked,
      hideLockButton,
      resetFiltersShape,
      selectedTab,
      tabs,
      details,
      toggleLockFilters,
      toggleDialogOpen,
      handleSetCustomCount,
    ],
  );

  const filtersValues = useMemo(
    () => ({
      ...filtersTool,
      count: { ...count, ...restCount },
      filtersQueryParams,
    }),
    [count, filtersQueryParams, filtersTool, restCount],
  );

  return (
    <FiltersContext.Provider value={filtersValues}>
      <DialogContext.Provider value={dialogValues}>{children}</DialogContext.Provider>
    </FiltersContext.Provider>
  );
};
